import {
  SUPPORT_FALLBACK_ERROR_MESSAGE,
  getUserFacingErrorMessage,
  handleAPIError,
} from '@/api/error-utils';
import {
  type AssetDataUploadResult,
  uploadAssetData,
  uploadAssetDataResponseSchema,
} from '@/asset/api/upload-asset-data';
import { CsvUpload } from '@/asset/components/asset-data-upload/csv-upload';
import { Results } from '@/asset/components/asset-data-upload/results';
import { Stepper } from '@/asset/components/asset-data-upload/stepper';
import { TemplateDownload } from '@/asset/components/asset-data-upload/template-download';
import type {
  ExpandedAssetsState,
  Step,
} from '@/asset/components/asset-data-upload/types';
import { useSupportError } from '@/hooks/use-support-error';
import { Box } from '@mui/material';
import { captureException, withScope } from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { ChangeEvent, useCallback, useRef, useState } from 'react';

interface AssetDataUploadProps {
  onClose: () => void;
}

export function AssetDataUpload(props: AssetDataUploadProps) {
  const queryClient = useQueryClient();
  const { showSupportError } = useSupportError();
  const [currentStep, setCurrentStep] = useState<Step>('download-template');
  const [file, setFile] = useState<File | null>(null);
  const [uploadResult, setUploadResult] =
    useState<AssetDataUploadResult | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadErrorMessage, setUploadErrorMessage] = useState<string | null>(
    null
  );
  const [uploadLineCount, setUploadLineCount] = useState<number | undefined>(
    undefined
  );
  const [uploadHeaders, setUploadHeaders] = useState<string[]>([]);
  const [expandedAssets, setExpandedAssets] = useState<ExpandedAssetsState>({
    updated: false,
    notUpdated: false,
    failed: false,
    structureErrors: false,
  });
  const [hasFatalError, setHasFatalError] = useState(false);

  const clearFile = () => {
    setFile(null);
    setUploadLineCount(undefined);
    setUploadHeaders([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const onClose = () => {
    props.onClose();
  };

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSelectFile = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  const handleFileChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const selectedFile = event.target.files?.[0];
      if (selectedFile) {
        setFile(selectedFile);
        setUploadErrorMessage(null);

        const reader = new FileReader();
        reader.onload = (e) => {
          const text = e.target?.result as string;
          const lines = text.split('\n').filter((line) => line.trim() !== '');
          setUploadLineCount(Math.max(0, lines.length - 1));

          const headerLine = lines[0];
          if (headerLine) {
            const headers = headerLine
              .split(',')
              .map((h) => h.trim().toLowerCase());
            setUploadHeaders(headers);
          }
        };
        reader.readAsText(selectedFile);
      }
    },
    []
  );

  const handleUpload = useCallback(async () => {
    if (!file) return;

    setIsUploading(true);
    setUploadErrorMessage(null);

    try {
      const formData = new FormData();
      formData.append('data', file);

      const result = await uploadAssetData(formData);
      clearFile();
      setUploadResult(result);
      setCurrentStep('results');
      queryClient.invalidateQueries({ queryKey: ['asset'] });
    } catch (error) {
      clearFile();
      const axiosError = error as AxiosError;

      // First try to parse as a response with validation errors
      const knownError = uploadAssetDataResponseSchema.safeParse(
        axiosError.response?.data
      );
      if (knownError.success) {
        // This is a known error that comes back due to the fact it rolls back the transaction if any of the rows are bad.
        // A transaction rollback always results in a 400 or 500-level status code
        // The knownError fails to parse when the data body is missing which happens on unexpected errors or initial validation errors

        // Only advance to results if we have either items or structure errors
        // If both are empty, stay on upload tab and show error
        const hasItems = knownError.data.data.items.length > 0;
        const hasStructureErrors =
          knownError.data.data.structureErrors.length > 0;

        if (hasItems || hasStructureErrors) {
          setUploadResult(knownError.data.data);
          setUploadErrorMessage(null);
          setCurrentStep('results');
          return;
        }
        // Both items and structureErrors are empty - show error on upload tab
        setUploadErrorMessage(
          'There was a problem with the file. Please check your file format and try again.'
        );
        return;
      }

      // Could not parse as validation errors - use generic error handler
      const apiError = handleAPIError(axiosError);

      // Curated codes from ERROR_MESSAGES are the handled set: show
      // inline, skip Sentry. Anything that falls through to the support
      // fallback is unhandled and must fire Sentry.
      const curated = getUserFacingErrorMessage(apiError);
      if (curated !== SUPPORT_FALLBACK_ERROR_MESSAGE) {
        setUploadErrorMessage(curated);
        return;
      }
      if (!apiError.sentToSentry) {
        withScope((scope) => {
          scope.setContext('upload', {
            fileSize: file?.size,
            lineCount: uploadLineCount,
          });
          captureException(error);
        });
      }
      setHasFatalError(true);
      showSupportError('An error occurred while updating asset data.');
    } finally {
      setIsUploading(false);
    }
  }, [file, queryClient, showSupportError, uploadLineCount]);

  const handleToggleExpanded = useCallback(
    (section: keyof ExpandedAssetsState) => {
      setExpandedAssets((prev) => ({
        ...prev,
        [section]: !prev[section],
      }));
    },
    []
  );

  const handleStartNew = useCallback(() => {
    setCurrentStep('download-template');
    setFile(null);
    setUploadResult(null);
    setUploadErrorMessage(null);
    setUploadLineCount(undefined);
    setUploadHeaders([]);
    setExpandedAssets({
      updated: false,
      notUpdated: false,
      failed: false,
      structureErrors: false,
    });
  }, []);

  const handleBack = useCallback(() => {
    if (currentStep === 'upload-csv') {
      setCurrentStep('download-template');
      setHasFatalError(false);
    } else if (currentStep === 'results') {
      setCurrentStep('upload-csv');
    }
  }, [currentStep]);

  const handleNext = useCallback(() => {
    if (currentStep === 'download-template') {
      setCurrentStep('upload-csv');
    }
  }, [currentStep]);

  return (
    <Box sx={{ p: 3 }}>
      <Stepper currentStep={currentStep} />

      {currentStep === 'download-template' && (
        <TemplateDownload onBack={onClose} onNext={handleNext} />
      )}

      {currentStep === 'upload-csv' && (
        <CsvUpload
          uploadErrorMessage={uploadErrorMessage}
          file={file}
          isUploading={isUploading}
          onSelectFile={handleSelectFile}
          onUpload={handleUpload}
          onBack={handleBack}
          onClose={onClose}
          hasFatalError={hasFatalError}
          uploadLineCount={uploadLineCount}
          uploadHeaders={uploadHeaders}
        />
      )}

      {currentStep === 'results' && (
        <Results
          uploadResult={uploadResult}
          expandedAssets={expandedAssets}
          onToggleExpanded={handleToggleExpanded}
          onBack={handleBack}
          onStartNew={handleStartNew}
          onClose={onClose}
        />
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
    </Box>
  );
}
